import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import useModalA11y from '../hooks/useModalA11y';
import './ScreenshotModal.css';

/**
 * ScreenshotModal - Full-screen modal for viewing captured screenshots
 *
 * Displays screenshots captured via the drag-to-select feature.
 * Shows the image at full resolution for detailed inspection.
 */
const ScreenshotModal = () => {
  const { featureManager } = useWebRTC();
  const [screenshot, setScreenshot] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef(null);

  // Subscribe to screenshot events
  useEffect(() => {
    if (!featureManager) return;

    const screenshotFeature = featureManager.getFeature('screenshot');
    if (!screenshotFeature) {
      console.log('[ScreenshotModal] Screenshot feature not found');
      return;
    }

    // Listen for captured screenshots
    const handleScreenshotCaptured = (data) => {
      console.log('[ScreenshotModal] Screenshot captured, showing modal');
      setScreenshot(data);
      setIsVisible(true);
    };

    // Listen for screenshot cleared
    const handleScreenshotCleared = () => {
      setScreenshot(null);
      setIsVisible(false);
    };

    const unsubCapture = screenshotFeature.on('screenshotCaptured', handleScreenshotCaptured);
    const unsubClear = screenshotFeature.on('screenshotCleared', handleScreenshotCleared);

    return () => {
      unsubCapture?.();
      unsubClear?.();
    };
  }, [featureManager]);

  // Handle close modal
  const handleClose = useCallback(() => {
    setIsVisible(false);
    // Clear after animation
    setTimeout(() => setScreenshot(null), 200);

    // Also clear from feature
    if (featureManager) {
      const screenshotFeature = featureManager.getFeature('screenshot');
      screenshotFeature?.clearLastScreenshot();
    }
  }, [featureManager]);

  // Focus trap + initial focus + focus restore + Escape-to-close
  useModalA11y({ isOpen: isVisible, onClose: handleClose, containerRef: modalRef });

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!screenshot) {
    return null;
  }

  const imageUrl = `data:${screenshot.mimeType};base64,${screenshot.imageData}`;
  const timestamp = new Date(screenshot.timestamp).toLocaleString();

  return (
    <div
      className={`screenshot-modal-backdrop ${isVisible ? 'visible' : ''}`}
      onClick={handleBackdropClick}
    >
      <div className="screenshot-modal-content" ref={modalRef} role="dialog" aria-modal="true">
        <div className="screenshot-modal-header">
          <div className="screenshot-info">
            <span className="screenshot-dimensions">
              {screenshot.width} x {screenshot.height}
            </span>
            <span className="screenshot-timestamp">
              {timestamp}
            </span>
            {screenshot.eventId && (
              <span className="screenshot-event-id">
                Event #{screenshot.eventId}
              </span>
            )}
          </div>
          <button className="screenshot-close-btn" onClick={handleClose} aria-label="Close screenshot">
            <i className="simple-icon-close" />
          </button>
        </div>

        <div className="screenshot-image-container">
          <img
            src={imageUrl}
            alt={`Screenshot ${screenshot.width}x${screenshot.height}`}
            className="screenshot-image"
          />
        </div>

        <div className="screenshot-modal-footer">
          <span className="screenshot-hint">
            Press <kbd>Esc</kbd> or click outside to close
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScreenshotModal;
